import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CircularProgress } from './CircularProgress';
import { motion } from 'framer-motion';
import { Grid, BookMarked, LayoutGrid, Layers, BarChart3 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useCollectionStore } from '../lib/collectionStore';
import { getAllSets } from '../services/pokemonTcgApi';
import { useProfileStore } from '../lib/supabase';

interface StatsData {
  totalCards: number;
  uniqueCards: number;
  setsCollected: number;
  seriesCollected: number;
  totalSeries: number;
  totalCardsInDatabase: number;
}

interface CollectionStatsProps {
  setId?: string;
  totalCards?: number;
  stats?: StatsData;
  activeTab?: 'total' | 'unique' | 'series' | 'portfolio';
  onTabChange?: (tab: 'total' | 'unique' | 'series' | 'portfolio') => void;
}

export default function CollectionStats({ setId, totalCards, stats, activeTab, onTabChange }: CollectionStatsProps) {
  const { collection } = useCollectionStore();
  const { isAuthenticated } = useProfileStore();
  const [setStats, setSetStats] = useState({
    uniqueCards: 0,
    totalCards: 0,
    uniqueFoils: 0,
    totalFoils: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [allSets, setAllSets] = useState<any[]>([]);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000;
  
  const fetchStats = useCallback(async () => {
    try {
      if (!isAuthenticated) {
        setSetStats({
          uniqueCards: 0,
          totalCards: 0,
          uniqueFoils: 0,
          totalFoils: 0
        });
        setLoading(false);
        return;
      }

      const sets = await getAllSets();
      setAllSets(sets);

      // Get unique card IDs (ignoring finishes)
      const uniqueCardIds = new Set(collection.map(card => card.cardId));
      
      // Calculate total cards including duplicates
      const totalCardCount = collection.reduce((sum, card) => sum + card.quantity, 0);

      // Calculate stats for specific set if setId is provided
      if (setId) {
        const setCards = collection.filter(card => card.setId === setId);
        const uniqueSetCardIds = new Set(setCards.map(card => card.cardId));
        
        setSetStats({
          uniqueCards: uniqueSetCardIds.size,
          totalCards: setCards.reduce((sum, card) => sum + card.quantity, 0),
          uniqueFoils: new Set(setCards.filter(card => card.finish !== 'normal').map(card => card.cardId)).size,
          totalFoils: setCards.filter(card => card.finish !== 'normal').reduce((sum, card) => sum + card.quantity, 0)
        });
      } else {
        // Calculate global stats
        const uniqueSeries = new Set(sets.map(set => set.series));
        const collectedSeries = new Set(collection.map(card => {
          const set = sets.find(s => s.id === card.setId);
          return set?.series;
        }));

        setSetStats({
          uniqueCards: uniqueCardIds.size,
          totalCards: totalCardCount,
          uniqueFoils: new Set(collection.filter(card => card.finish !== 'normal').map(card => card.cardId)).size,
          totalFoils: collection.filter(card => card.finish !== 'normal').reduce((sum, card) => sum + card.quantity, 0)
        });
      }

      setLoading(false);
      setError(null);
    } catch (error) {
      console.error('Error fetching collection stats:', error);
      
      if (retryCount < MAX_RETRIES) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
        }, RETRY_DELAY);
      } else {
        setError(error instanceof Error ? error.message : 'Error loading stats');
      }
    } finally {
      setLoading(false);
    }
  }, [setId, collection, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
    }
  }, [setId, collection, fetchStats, isAuthenticated]);

  const completion = setId
    ? totalCards && totalCards > 0 ? (setStats.uniqueCards / totalCards) * 100 : 0
    : stats?.totalCardsInDatabase > 0 ? (stats.uniqueCards / stats.totalCardsInDatabase) * 100 : 0;

  if (error) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-lg p-4">
        <p className="text-red-500">Error loading collection stats</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="bg-black/95 backdrop-blur-sm border border-white/10 rounded-lg p-4">
        <div className="text-center">
          <p className="text-white/60 mb-2">Sign in to track your collection progress</p>
          <Link
            to="/auth"
            className="inline-block px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black/95 backdrop-blur-sm border border-white/10 rounded-lg p-3">
      <div className="space-y-3">
        {onTabChange ? (
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => onTabChange('total')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                activeTab === 'total' ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white'
              }`}
            >
              <Grid size={16} />
              <span>All Cards</span>
            </button>
            <button
              onClick={() => onTabChange('unique')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                activeTab === 'unique' ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white'
              }`}
            >
              <BookMarked size={16} />
              <span>Unique</span>
            </button>
            <button
              onClick={() => onTabChange('series')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                activeTab === 'series' ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white'
              }`}
            >
              <Layers size={16} />
              <span>Series</span>
            </button>
            <button
              onClick={() => onTabChange('portfolio')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                activeTab === 'portfolio' ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white'
              }`}
            >
              <BarChart3 size={16} />
              <span>Portfolio</span>
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold">Collection Progress</h2>
            <span className="text-sm text-white/60">
              {setId ? `${setStats.uniqueCards} / ${totalCards} cards` : `${stats?.uniqueCards || 0} cards`}
            </span>
          </div>
        )}
        
        <div className="relative h-2 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            className={`absolute inset-y-0 left-0 ${loading ? 'bg-white/20' : 'bg-green-500'} rounded-full transition-all duration-500`}
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(completion, 100)}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
          {loading && (
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-shimmer" />
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            {
              label: 'Total Owned',
              value: setId ? setStats.totalCards : stats?.totalCards || 0
            },
            {
              label: 'Series',
              value: setId ? '-' : `${setStats.collectedSeries || 0} / ${setStats.totalSeries || 0}`
            },
            {
              label: 'Sets',
              value: setId ? '-' : `${stats?.setsCollected || 0} sets`
            },
            {
              label: 'Completion',
              value: `${(completion || 0).toFixed(2)}%`
            }
          ].map(({ label, value }) => (
            <div
              key={label}
              className={`p-2 rounded-lg border border-white/10 ${
                loading ? 'animate-pulse bg-white/5' : 'bg-white/5'
              }`}
            >
              <h3 className="text-xs font-medium text-white/60">{label}</h3>
              <p className="text-lg font-bold">{value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}